import React, { useState, useEffect } from 'react';
import { Form, Row, Col } from 'reactstrap';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import BasicFormContainer from 'components/layout/BasicFormContainer';
import translations from 'services/translations/translations';
import TextInputField from 'components/layout/forms/TextInputField';
import getTargetValue from 'services/forms/getTargetValue';
import { connect } from 'react-redux';
import { fetchOrganizationUnits } from 'store/users/actions';
import IOrganizationUnit from 'interfaces/users/IOrganizationUnit';
import PageContainer from 'components/layout/PageContainer';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import ModalAccept from 'components/layout/modals/ModalAccept';
import ModalConfirm from 'components/layout/modals/ModalConfirm';
import SelectInputFieldControlled from 'components/layout/forms/SelectInputFieldControlled';
import BasicTableNoPagination from 'components/layout/tables/BasicTableNoPagination';
import IOrganizationUnitPost from 'interfaces/users/IOrganizationUnitPost';

import userApi from '../../api/userManagement';
import { showOrganizationUnitsDropdown } from './showOrganizationUnitsDropdown';

import 'style/userManagement/OrganizationUnit.css';

const OrganizationUnit = ({
  organizationUnitTypesList,
  fetchOrganizationUnitTypesList,
}: {
  organizationUnitTypesList: IOrganizationUnit[];
  fetchOrganizationUnitTypesList: () => void;
}): JSX.Element => {
  const { t } = useTranslation([
    translations.common,
    translations.admin,
    translations.userManagement,
    translations.contacts,
  ]);
  const history = useHistory();
  const { orgId } = useParams();
  const [modalDelete, setModalDelete] = useState(false);
  const [parentId, setParentId] = useState<number>(0);
  const [name, setName] = useState<string>('');
  const [companyName, setCompanyName] = useState('');
  // FI or SE: the money of the offers of the unit (€ or SEK)
  const [country, setCountry] = useState('FI');
  const [vatNumber, setVatNumber] = useState('');
  const [children, setChildren] = useState<{ name: string; id: number }[]>([]);
  const toggleDelete = () => setModalDelete(!modalDelete);
  const [modalDeleteConfirmation, setModalDeleteConfirmation] = useState(false);
  const toggleDeleteConfirmation = () =>
    setModalDeleteConfirmation(!modalDeleteConfirmation);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  // Why the unit cannot be taken out of use (its users), null when it can
  const [inUseText, setInUseText] = useState<null | string>(null);
  // The unit is out of use, and the result of taking it out of or into use
  const [disabled, setDisabled] = useState(false);
  const [resultText, setResultText] = useState('');
  const [orgSaved, setOrgSaved] = useState(false);
  const [success, setSuccess] = useState(false);
  const modalMessage = success
    ? t(`${translations.userManagement}:Organization unit saved`)
    : t(`${translations.userManagement}:Organization unit failed`);
  const [formError, setFormError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    fetchOrganizationUnitTypesList();
  }, [fetchOrganizationUnitTypesList]);

  useEffect(() => {
    // If not undefined there is a user to modify
    if (orgId !== undefined) {
      const submit = async () => {
        try {
          const result: IOrganizationUnit =
            await userApi.organizationUnits.getSingle(orgId);
          if (result !== undefined) {
            setName(result.name);
            setChildren(result.children);
            setCompanyName(result.companyName ? result.companyName : '');
            setDisabled(result.disabled === true);
            setCountry(result.country ?? 'FI');
            setVatNumber(result.vatNumber ?? '');
          }
        } catch (error) {
          // define action
        }
      };
      submit().catch((e) => console.log(e));
    }
  }, [orgId]);

  const goPreviousPage = () => {
    history.push('/users');
  };

  const addUpdateOrganizationUnit = () => {
    if (name === '') {
      setErrorMessage(t(`${translations.common}:Fill all fields`));
      setFormError(true);
    } else {
      const formattedOrg: IOrganizationUnitPost = {
        companyName: companyName === '' ? null : companyName,
        country,
        vatNumber: vatNumber.trim() === '' ? null : vatNumber.trim(),
        name,
        parentId: parentId === 0 ? null : parentId,
      };
      const submit = async () => {
        let result;
        if (orgId !== undefined) {
          try {
            result = await userApi.organizationUnits.put(formattedOrg, orgId);
            setOrgSaved(true);
            setSuccess(true);
          } catch (error) {
            setOrgSaved(true);
            setSuccess(false);
          }
        } else {
          try {
            result = await userApi.organizationUnits.post(formattedOrg);
            setOrgSaved(true);
            setSuccess(true);
          } catch (error) {
            setOrgSaved(true);
            setSuccess(false);
          }
        }
        if (result !== undefined && result.succeeded === true) {
          goPreviousPage();
        }
      };
      submit().catch((e) => console.log(e));
    }
  };

  const selectOrganizationUnit = (e: any) => {
    setParentId(parseInt(getTargetValue(e), 10));
  };

  // The unit is taken out of use or back into use, never removed: its offers, projects etc.
  // keep it in their history
  const setUnitDisabled = async (nextDisabled: boolean) => {
    try {
      await userApi.organizationUnits.setDisabled(orgId, nextDisabled);
      setDisabled(nextDisabled);
      setDeleteSuccess(true);
      setResultText(
        t(
          `${translations.userManagement}:disable.${
            nextDisabled ? 'Taken out of use' : 'Taken into use'
          }`
        )
      );
    } catch (error: any) {
      setDeleteSuccess(false);
      const message = error?.response?.data?.message;
      setResultText(
        t(
          `${translations.userManagement}:disable.errors.${
            message ?? 'Failed'
          }`,
          {
            defaultValue: t(
              `${translations.userManagement}:disable.errors.Failed`
            ),
          }
        )
      );
    }
    setModalDelete(false);
    setModalDeleteConfirmation(true);
  };

  // Out of use only without users: they are moved or removed first
  const onToggleUse = async () => {
    if (disabled) {
      setUnitDisabled(false);
      return;
    }

    try {
      const usage = await userApi.organizationUnits.usage(orgId);
      if (usage.users > 0) {
        setInUseText(
          t(`${translations.userManagement}:disable.Has users`, {
            count: usage.users,
          })
        );
        return;
      }
    } catch (error) {
      // The server refuses a unit with users in any case
    }
    setModalDelete(true);
  };

  const setSelectedRow = (selectedRow: any) => {
    if (selectedRow !== undefined) {
      history.push(`/organizationunits/${children[selectedRow].id}`);
    }
  };

  const orgUnitColumns = [
    {
      dataField: 'name',
      text: t(`${translations.common}:Subworkgroup`),
    },
  ];

  const isRootLevel = () =>
    organizationUnitTypesList.some(
      (org: IOrganizationUnit) => org.name === name
    );

  return (
    <>
      <PageContainer>
        <p className="header-text">{t('admin:Organization Units')}</p>
        <BasicFormContainer>
          <Form className="form-spacing">
            <Row>
              <Col>
                <TextInputField
                  value={name}
                  label={t(`${translations.common}:Name`)}
                  required
                  id="name"
                  name="name"
                  onChange={(e: any) => setName(e.target.value)}
                />
              </Col>
              {(orgId !== undefined && isRootLevel()) ||
              (orgId === undefined && parentId === 0) ? (
                <Col>
                  <TextInputField
                    value={companyName}
                    label={t(`${translations.contacts}:Company Name`)}
                    required
                    id="companyName"
                    name="companyName"
                    onChange={(e: any) => setCompanyName(e.target.value)}
                  />
                </Col>
              ) : null}
              <Col>
                <SelectInputFieldControlled
                  value={country}
                  id="country"
                  label={t(`${translations.userManagement}:Country`)}
                  onChange={(e: any) => setCountry(getTargetValue(e))}
                  name="country"
                  required
                  options={['FI', 'SE'].map((code) => (
                    <option key={code} value={code}>
                      {t(`${translations.userManagement}:countries.${code}`)}
                    </option>
                  ))}
                />
              </Col>
              <Col>
                <TextInputField
                  value={vatNumber}
                  label={t(`${translations.userManagement}:VAT number`)}
                  id="vatNumber"
                  name="vatNumber"
                  onChange={(e: any) => setVatNumber(e.target.value)}
                />
              </Col>
              {orgId === undefined ? (
                <Col>
                  <SelectInputFieldControlled
                    value={parentId}
                    id="orgUnits"
                    label={t(
                      `${translations.userManagement}:Parent organization unit`
                    )}
                    onChange={selectOrganizationUnit}
                    name="orgUnits"
                    required
                    options={showOrganizationUnitsDropdown(
                      organizationUnitTypesList,
                      t
                    )}
                  />
                </Col>
              ) : (
                ''
              )}
            </Row>
          </Form>
          <Row>
            <Col>
              <PrimaryButton onClick={goPreviousPage}>
                {t(`${translations.common}:Back`)}
              </PrimaryButton>

              {/* Out of use instead of removed, so the history keeps the unit */}
              {orgId !== undefined ? (
                <PrimaryButton
                  className="add-button-margin"
                  onClick={onToggleUse}
                >
                  {t(
                    `${translations.userManagement}:disable.${
                      disabled ? 'Take into use' : 'Take out of use'
                    }`
                  )}
                </PrimaryButton>
              ) : (
                ''
              )}
              {disabled ? (
                <span className="organization-unit-disabled">
                  {t(`${translations.userManagement}:disable.Out of use`)}
                </span>
              ) : null}

              <PrimaryButton
                className="add-button-margin float-right"
                onClick={addUpdateOrganizationUnit}
              >
                {t(`${translations.common}:Save`)}
              </PrimaryButton>
            </Col>
          </Row>
          <ModalConfirm
            showing={modalDelete}
            setShowing={toggleDelete}
            header={t(`${translations.userManagement}:disable.Take out of use`)}
            text={t(`${translations.userManagement}:disable.Confirm`)}
            yesButton={t('common:Confirm')}
            noButton={t('common:Cancel')}
            confirm={() => setUnitDisabled(true)}
          />
          <ModalAccept
            showing={inUseText !== null}
            setShowing={() => setInUseText(null)}
            header={t(`${translations.userManagement}:disable.Take out of use`)}
            text={inUseText ?? ''}
          />
          <ModalAccept
            showing={modalDeleteConfirmation}
            // Taken out of use: back to the list of the units
            setShowing={() => {
              toggleDeleteConfirmation();
              if (deleteSuccess && disabled) {
                goPreviousPage();
              }
            }}
            header=""
            text={resultText}
          />
          <ModalAccept
            showing={orgSaved}
            setShowing={setOrgSaved}
            header=""
            text={modalMessage}
          />
          <ModalAccept
            showing={formError}
            setShowing={setFormError}
            header=""
            text={errorMessage}
          />
        </BasicFormContainer>
      </PageContainer>
      {orgId !== undefined ? (
        <BasicTableNoPagination
          columns={orgUnitColumns}
          data={children}
          keyField="id"
          setSelectedRow={setSelectedRow}
        />
      ) : null}
    </>
  );
};
const mapStateToProps = (state: any) => ({
  organizationUnitTypesList: state.users.organizationUnits,
});

const mapDispatchToProps = (dispatch: any) => ({
  fetchOrganizationUnitTypesList: () => dispatch(fetchOrganizationUnits()),
});
export default connect(mapStateToProps, mapDispatchToProps)(OrganizationUnit);
